
import WDUtil from '../wd-util';
import WDError from '../wd-error';
import { WDProxy } from '../wd-proxy';
import WDFetchError from './wd-fetch-error';

/**
	WDFetch
	*/

export default class WDFetch extends WDProxy
{
	#controller = null;
	#config = {
		contentType: 'application/json',
		auth: {},
		reqURL: null,
		reqURI: null,
		baseObject: null,
		allowMultipleRequests: false,
		headers: {},
	};

	constructor(config, values)
	{
		super({});

		if (typeof (config) == 'string')
		{
			this.#config.reqURI = config;
		}
		else if (typeof (config) == 'object')
		{
			WDUtil.merge(this.#config, config);
		}

		if (values != undefined) this.add(values);
	}

	/**
	 * since we inherit WDProxy, we can't use normal inheritance
	 * @returns 
	 */
	getConfig() 
	{
		return this.#config;
	};

	getReqURI() 
	{ 
		return this.#config.reqURI || ''; 
	};

	setReqURI(uri) {
		this.#config.reqURI = uri;
		return this;
	}

	getReqURL() { return this.#config.reqURL || ''; };
	setReqURL(url)
	{
		this.#config.reqURL = url;
		return this;
	};

	//backwards compatiblity - for now
	add(obj)
	{
		return this.assign(obj);
	};

	abort()
	{
		if (WDConfig?.debug?.fetch) console.log('Aborting request');
		this.#controller.abort('queue');
	};

	buildURI(addURI)
	{
		let uri = this.#config.reqURI;
		if (addURI) uri += addURI;
		return uri;
	};

	buildURL(uri)
	{
		const url = String(this.getReqURL() + this.buildURI(uri)).trim();

		try
		{
			return new URL(url);
		}
		catch (err)
		{
			if (WDConfig?.debug?.fetch) console.log('Error forming URL', url, err);
		}
	};

	buildHeaders()
	{
		const headers = {
			'Content-Type': this.#config.contentType,
			'X-Requested-With': 'XMLHttpRequest'
		};

		this.buildAuth();

		Object.assign(headers, this.#config.headers);

		return new Headers(headers);
	};

	buildAuth()
	{
		if (this.#config.auth)
		{
			const auth = this.#config.auth;

			if (auth.mode == 'basic')
			{
				this.#config.headers.Authorization = 'Basic ' + btoa(auth.login + ':' + auth.password);
			}
			else if (auth.mode == 'digest')
			{
			}
		}
	};


	buildRequest(method, uri)
	{
		//setup a controller so we can do aborts
		this.#controller = (this.#config.allowMultipleRequests === false) ? new AbortController() : null;

		//create a base url
		const url = this.buildURL(uri);

		if (typeof (method) == 'undefined') method = 'GET';

		//options for the Request object
		const options = this.buildOptions(method, url);

		//return the request
		return new Request(url, options);
	};

	buildOptions(method, url)
	{
		const options = {
			method: method,
			headers: this.buildHeaders()
		};

		if (this.#controller !== null) options.signal = this.#controller.signal;
		if (this.#config.auth != false) options.credentials = 'include';

		const dataObj = this.toObject();
		const dataKeys = Object.keys(dataObj);

		if (dataKeys.length > 0)
		{
			//if it's a method that can have a body, add the body	
			if (method == 'PUT' && dataObj.file)
			{
				options.body = dataObj.file;
			}
			else if (method == 'GET')
			{
				dataKeys.forEach(key => url.searchParams.append(key, dataObj[key]));
			}
			else
			{
				options.body = JSON.stringify(dataObj);
			}
		}

		return options;

	};

	buildResponseContentType(response)
	{
		const ct = response.headers.get("content-type");
		const pos = ct.indexOf(';');

		return (pos !== -1) ? ct.substr(0, pos) : ct;
	};

	req(method, uri)
	{
		if (WDConfig?.debug?.fetch)
		{
			console.log('Running ' + method + ' request', this.#config.reqURI, uri);
			console.log('Request Data', this.toObject());
			if (this.trace_call) console.trace();
		}

		//cancel any current request
		if (this.#controller) this.abort();

		//build the request
		const request = this.buildRequest(method, uri);

		//for some error handling later
		let cloneResp = null;

		//run the fetch
		const ftch = fetch(request).then(response =>
		{
			//clone the response for error handling
			cloneResp = response.clone();
			const contentType = this.buildResponseContentType(response);
		
			if (response.ok)
			{
				//everything came back okay, and it was the right response type
				if (contentType == this.#config.contentType)
				{
					return (contentType == 'application/json') ? response.json() : response.text();
				}
				//usually some sort of server side parse error
				else
				{
					throw new WDFetchError(response.message, { code: response.status, response: response });
				}
			}
			//this is usually a caught error, formatted properly
			else
			{
				throw new WDError(response.message,
					{
						code: response.status,
						name: 'FetchError',
						contentType: contentType,
						response: cloneResp
					});
			}
		})
			.catch(err =>
			{

				console.log('Error On Request', this.#config.reqURI);
				//do nothing on abort errors
				if (err.name == 'AbortError') 
				{
					throw new Error('AbortError');
				}
				//auth errors get special treatment
				else if (err.code == 401)
				{
					throw new Error('Unauthorized', { code: err.code });
				}
				//auth errors get special treatment
				else if (err.code == 403)
				{
					throw new Error('Forbidden', { code: err.code });
				}
				else if (err.name == 'FetchError')
				{
					const wdClone = cloneResp.clone();

					//once for the console				
					cloneResp.text().then(str =>
					{
						if (WDConfig?.debug?.fetch) console.log(err.name, str);
					});

					//throw another copy of the error to a catch later in the chain
					throw new WDError(err.message,
						{
							code: err.status,
							name: err.name,
							contentType: err.contentType,
							response: wdClone
						});

				}
				else if (err.name == 'SyntaxError')
				{
					cloneResp.text().then(str =>
					{
						if (WDConfig?.debug?.fetch) console.log(err.name, str);
					});
				}
				//flush out the response for more detailed info on what happened
				else
				{
					//throw another copy of the error to a catch later in the chain
					throw new WDError(err.message,
						{
							code: err.status,
							name: err.name,
							contentType: err.contentType,
							response: err.response
						});
				}

			})
			.finally(data =>
			{
				this.#controller = null;
			});

		return ftch;

	};


	//wrappers for sending to the api
	post(uri) { return this.req('POST', uri); };
	put(uri) { return this.req('PUT', uri); };
	del(uri) { return this.req('DELETE', uri); };

};

